import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine,
  Cell, LabelList, Tooltip, ResponsiveContainer,
} from 'recharts';
import api from '../utils/api';
import DateRangeFilter from '../components/DateRangeFilter';
import ChartCard from '../components/ChartCard';
import KPICard from '../components/KPICard';
import InfoTip from '../components/InfoTip';
import ExportButton from '../components/ExportButton';
import { formatCurrency, formatPercent, formatDuration, getDatePreset } from '../utils/formatters';
import useIsMobile from '../hooks/useIsMobile';
import { downloadBlob, waitForCharts } from '../utils/svgExport';
import { composeReportImage, REPORT_CHART_W, REPORT_FULL_CHART_W } from '../utils/reportImage';

const NET_PROFIT_INFO =
  'Net Sales minus COGS.|Does not deduct TikTok fees, ad spend, shipping, or affiliate commission.';

const FILL_TIME_INFO =
  'FILL TIME — how long a break takes to sell out: from the first spot being filled '
  + 'to the last spot selling. | Per-break, NOT per-LIVE — one TikTok LIVE contains '
  + 'several breaks. | Excludes rip time. Breaks filled in a single pass, or taking '
  + 'over 12 hours, are left out.';

const RIP_TIME_INFO =
  'RIP & RECAP — from the first card logged in Card(s) to the breaker pressing End '
  + 'Break after the recap. | The recap is counted inside this segment. | Breaks '
  + 'where no cards were logged, or where End Break was never pressed, are left out.';

const BREAK_TOTAL_INFO =
  'BREAK TOTAL — the whole break end to end: first spot filled to End Break pressed. '
  + '| Needs End Break to have been pressed, so a break the breaker forgot to end is '
  + 'left out of this average entirely.';

const MIX_PAR_INFO =
  'MARGIN VS SAME-MIX PAR — how much margin a breaker gets out of the same products, '
  + 'compared with everyone else. | Raw Profit Margin mostly shows which products a '
  + 'breaker was given. Here each break is only compared with breaks of the same '
  + 'product mix (freebies aside). | Par for a mix is the margin the OTHER breakers '
  + 'got on it in this date range. +3 pts means 3 points more margin than par. | '
  + 'Freebies such as high bid packs stay in each break\'s COGS, so a pack counts '
  + 'in its favour only if the extra sales covered it. | A mix counts only when other '
  + 'breakers ran it at least twice in the range; the rest are left out.';

const formatCount = (v) => Number(v ?? 0).toLocaleString('en-US');

// Signed, so a bar below par reads as a shortfall and not a small margin.
const formatPts = (v) => (v == null ? '--' : `${v > 0 ? '+' : ''}${Number(v).toFixed(1)} pts`);
const formatSignedCurrency = (v) => (v == null ? '--' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${formatCurrency(Math.abs(v))}`);

// Tooltip lines for the par chart: the bar is a difference, so show the two
// margins it came from, what it was worth, and how much of the breaker's
// selling it actually covers.
function mixParDetail(row) {
  return [
    `Their margin ${formatPercent(row.mix_margin_pct)} vs par ${formatPercent(row.mix_par_margin_pct)}`,
    `${formatSignedCurrency(row.mix_profit_vs_par)} profit vs par`,
    `${formatCount(row.mix_breaks_compared)} of ${formatCount(row.break_count)} breaks compared `
      + `(${Math.round(row.mix_sales_coverage_pct ?? 0)}% of net sales)`,
  ];
}

// One horizontal bar chart per metric. `fmt` drives both the bar label and the
// tooltip so a dollar metric never renders as a bare number.
// `share` marks the metrics that carry a "% of total" beside the value. The
// denominator is always the company-wide figure for that metric from `totals`.
// For Net Sales, Net Profit and Breaks that equals the sum of the bars, so the
// slices add to 100%. Live Days is deliberately different: the company's live
// days are distinct calendar dates, so two breakers live on the same day each
// get full credit for it and the slices add to more than 100%. The averages
// carry no share at all — a slice of a sum of averages is not a real quantity.
const METRICS = [
  { key: 'net_sales',                  label: 'Net Sales',                color: '#22c55e', fmt: formatCurrency, share: true },
  { key: 'avg_net_sales_per_break',    label: 'Avg Net Sales / Break',    color: '#14b8a6', fmt: formatCurrency },
  { key: 'net_profit',                 label: 'Net Profit',               color: '#3b82f6', fmt: formatCurrency, share: true, info: NET_PROFIT_INFO },
  { key: 'avg_net_profit_per_break',   label: 'Avg Net Profit / Break',   color: '#6366f1', fmt: formatCurrency, info: NET_PROFIT_INFO },
  { key: 'margin_pct',                 label: 'Profit Margin',            color: '#8b5cf6', fmt: formatPercent },
  { key: 'mix_vs_par_pts',             label: 'Margin vs Same-Mix Par',   color: '#0d9488', fmt: formatPts, info: MIX_PAR_INFO,
    omitNull: true, detail: mixParDetail, emptyText: 'No product mix was run by enough other breakers in this range to set a par.',
    note: 'Margin on the same product mix as other breakers, in points. Below par shows red. Hover for the dollars and how many breaks were compared.' },
  { key: 'avg_net_sales_per_live_day', label: 'Avg Net Sales / Live Day', color: '#ec4899', fmt: formatCurrency },
  { key: 'break_count',                label: 'Breaks',                   color: '#f59e0b', fmt: formatCount, share: true },
  { key: 'live_days',                  label: 'Live Days',                color: '#0ea5e9', fmt: formatCount, share: true,
    note: "Share of the company's live days. Breakers live on the same day each get full credit, so these don't add up to 100%." },
  { key: 'avg_fill_seconds',           label: 'Avg Fill Time',            color: '#64748b', fmt: formatDuration, info: FILL_TIME_INFO,
    ascending: true, omitNull: true,
    note: 'Fastest first — a shorter bar is better here. Breakers with no measurable fill are left off.' },
  { key: 'avg_rip_seconds',            label: 'Avg Rip & Recap',          color: '#a16207', fmt: formatDuration, info: RIP_TIME_INFO,
    ascending: true, omitNull: true,
    note: 'Fastest first — a shorter bar is better here. Breakers with no measurable rip are left off.' },
  { key: 'avg_break_total_seconds',    label: 'Avg Break Total',          color: '#78716c', fmt: formatDuration, info: BREAK_TOTAL_INFO,
    ascending: true, omitNull: true,
    note: 'Fastest first — a shorter bar is better here. Needs End Break to have been pressed.' },
];

// The summary strip, defined once so the page and the exported image always
// show the same figures.
function summaryTiles(totals) {
  if (!totals) return [];
  return [
    { label: 'Net Sales',     value: formatCurrency(totals.net_sales),  info: null },
    { label: 'Net Profit',    value: formatCurrency(totals.net_profit), info: NET_PROFIT_INFO },
    { label: 'Profit Margin', value: formatPercent(totals.margin_pct),  info: null },
    { label: 'Breaks',        value: formatCount(totals.break_count),   info: null },
    { label: 'Live Days',     value: formatCount(totals.live_days),     info: null },
    { label: 'Breakers',      value: formatCount(totals.breaker_count), info: null },
    { label: 'Avg Fill Time', value: formatDuration(totals.avg_fill_seconds), info: FILL_TIME_INFO },
    { label: 'Avg Rip & Recap', value: formatDuration(totals.avg_rip_seconds), info: RIP_TIME_INFO },
    { label: 'Avg Break Total', value: formatDuration(totals.avg_break_total_seconds), info: BREAK_TOTAL_INFO },
  ];
}

// This breaker's slice of the company total, e.g. "30%". `total` is null on
// metrics where a share would be meaningless, which suppresses it entirely.
function shareText(value, total) {
  if (!total || total <= 0) return null;
  const pct = (value / total) * 100;
  if (pct > 0 && pct < 1) return '<1%';   // don't round a real slice down to 0%
  return `${Math.round(pct)}%`;
}

// The value as drawn: the metric's own formatting, then its share of the total
// where one applies, then any extra the chart asks for (e.g. a break count).
function labelValue(row, metricKey, fmt, shareTotal, extra) {
  const share = shareText(row[metricKey], shareTotal);
  const more = extra ? extra(row) : null;
  return [fmt(row[metricKey]), share, more].filter(Boolean).join(' • ');
}

// Labels are measured rather than estimated from a per-character average: the
// name and the value are drawn as two separate <text> elements, so the second
// one's x depends on the first one's exact width.
const LABEL_SIZE = 12;
const LABEL_FONT = "'Inter', -apple-system, sans-serif";   // matches body in App.css
const SEP = ' · ';

let measureCtx = null;
function textWidth(text, weight = 400) {
  if (!measureCtx) measureCtx = document.createElement('canvas').getContext('2d');
  measureCtx.font = `${weight} ${LABEL_SIZE}px ${LABEL_FONT}`;
  return measureCtx.measureText(String(text ?? '')).width;
}

// Space kept clear to the right of the plot for labels that can't fit inside
// their bar. Reserving it unconditionally is what keeps those labels on the
// card: a bar can end at the plot edge, so without a gutter its label runs off
// the side. Names are trimmed to fit it (the tooltip still has the full one).
const GUTTER = { wide: 200, compact: 118 };

// How much room a label sitting just past its bar actually has.
//
// Recharts never hands a label the plot's geometry, but the bar carries it:
// bars run from the zero baseline, so barWidth/|value| is pixels-per-unit, and
// the domain's top end says how far the plot reaches. `gutter` is the chart's
// right margin, so plotRight + gutter is the SVG's own right edge. Deriving it
// this way keeps the chart hook-free — it has to stay a plain function so its
// ResponsiveContainer remains ChartCard's direct child for PNG export.
//
// Budgeting against the gutter alone is what used to cut names short: the
// gutter is only what's left when a bar spans the whole plot, and outside
// labels belong to the *short* bars, which have most of the plot free too.
function availableTextWidth({ left, right, barWidth, value, domainMax, gutter }) {
  const worstCase = gutter - 12;
  const magnitude = Math.abs(value);
  if (!magnitude || barWidth <= 0 || !(domainMax > 0)) return worstCase;
  const perUnit = barWidth / magnitude;
  const zeroX = value >= 0 ? left : right;
  const plotRight = zeroX + domainMax * perUnit;
  return Math.max(worstCase, plotRight + gutter - right - 12);
}

// Trim a breaker's name so `name · value` fits `maxPx`. Returns null when even
// a short name won't fit, in which case only the value is drawn.
function fitName(name, valueText, maxPx) {
  const budget = maxPx - textWidth(valueText) - textWidth(SEP);
  if (budget >= textWidth(name, 600)) return name;
  let trimmed = name;
  while (trimmed.length > 1 && textWidth(trimmed + '…', 600) > budget) {
    trimmed = trimmed.slice(0, -1);
  }
  return trimmed.length > 1 ? trimmed + '…' : null;
}

// A card-coloured outline behind text that sits outside its bar, so a
// reference line crossing the label breaks around it instead of striking
// through it. A literal white rather than var(--bg-card): it has to survive
// being serialised into the exported image.
const HALO = { stroke: '#fff', strokeWidth: 3, strokeLinejoin: 'round', paintOrder: 'stroke' };

// Name and value are drawn onto the bar itself rather than on a Y axis, so the
// ranking reads as a single strip per breaker. Very short (or zero, or
// negative) bars can't hold the text, so those fall back to a label sitting
// just past the bar in body text.
function BarLabel({ x, y, width, height, index, rows, metricKey, fmt, shareTotal, extra, gutter, domainMax }) {
  const row = rows[index];
  if (!row) return null;
  const name = row.name || 'Unassigned';
  const text = labelValue(row, metricKey, fmt, shareTotal, extra);
  const cy = y + height / 2;
  // Recharts hands the label a *signed* width for bars that run left of the
  // zero baseline, and an `x` at the baseline rather than the bar's left edge.
  // Normalise to real edges, or negative bars measure as "too narrow" and the
  // fallback label lands on top of the bar it was meant to sit beside.
  const left = Math.min(x, x + width);
  const barWidth = Math.abs(width);
  const right = left + barWidth;
  const needed = textWidth(name, 600) + textWidth(text, 600) + 34;

  if (barWidth >= needed) {
    return (
      <g>
        <text x={left + 10} y={cy} fill="#fff" fontSize={LABEL_SIZE} fontWeight={600} dominantBaseline="central">{name}</text>
        <text x={right - 10} y={cy} fill="#fff" fontSize={12} fontWeight={600} textAnchor="end" dominantBaseline="central">{text}</text>
      </g>
    );
  }

  const value = Number(row[metricKey]) || 0;
  const room = availableTextWidth({ left, right, barWidth, value, domainMax, gutter });

  // The pair won't fit, but a wide bar can still carry the name on its own —
  // send just the value outside. Without this a full-width bar on a narrow
  // screen loses its name entirely and the ranking stops being readable.
  if (barWidth >= textWidth(name, 600) + 20) {
    return (
      <g>
        <text x={left + 10} y={cy} fill="#fff" fontSize={LABEL_SIZE} fontWeight={600} dominantBaseline="central">{name}</text>
        <text x={right + 8} y={cy} fill="var(--text-secondary)" {...HALO} fontSize={LABEL_SIZE} fontWeight={400} dominantBaseline="central">{text}</text>
      </g>
    );
  }

  // Drawn as two <text> elements rather than one with a bold <tspan>. A tspan
  // inherits its size and baseline from the parent, and that inheritance does
  // not survive serialisation into a standalone SVG on every renderer — it came
  // out a size larger and sitting high in an exported image. Two elements, each
  // stating its own size, weight and baseline, cannot drift apart.
  const shown = fitName(name, text, room);
  const nameW = shown ? textWidth(shown, 600) : 0;
  // One space of gap; the value run carries the separator itself.
  const valueX = right + 8 + (shown ? nameW + textWidth(' ') : 0);
  return (
    <g>
      {shown && (
        <text x={right + 8} y={cy} fill="var(--text-secondary)" {...HALO}
          fontSize={LABEL_SIZE} fontWeight={600} dominantBaseline="central">{shown}</text>
      )}
      <text x={valueX} y={cy} fill="var(--text-secondary)" {...HALO}
        fontSize={LABEL_SIZE} fontWeight={400} dominantBaseline="central">{shown ? `·\u00a0${text}` : text}</text>
    </g>
  );
}

function RankedTooltip({ active, payload, metricKey, fmt, shareTotal, extra, detail }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div style={{
      background: 'var(--sidebar-bg, #1a202c)', color: 'white', padding: '6px 10px',
      borderRadius: 6, fontSize: 12, boxShadow: 'var(--shadow-lg)',
    }}>
      <div style={{ fontWeight: 600 }}>{row.name || 'Unassigned'}</div>
      <div>{labelValue(row, metricKey, fmt, shareTotal, extra)}</div>
      {detail && detail(row).map(line => <div key={line} style={{ opacity: 0.8 }}>{line}</div>)}
    </div>
  );
}

// A plain function, not a component, so the ResponsiveContainer it returns is
// ChartCard's own child — ChartCard looks for one by type to re-lay-out the
// chart on its off-screen PNG export stage, and a wrapper component hides it.
// `reference` ({ value, label }) draws a dashed line across the bars, such as
// everyone's combined figure, for each bar to be read against.
function rankedBarChart({ rows, totals, metricKey, color, fmt, share, compact, ascending, omitNull, detail, emptyText, extra, reference }) {
  if (!rows || rows.length === 0) {
    return <div style={{ padding: '20px 0', color: 'var(--text-light)', fontSize: 13, textAlign: 'center' }}>No data for this range.</div>;
  }

  // A metric with no value for a breaker (e.g. nobody's breaks had a
  // trustworthy fill span) must not plot as a zero bar — on a duration that
  // would read as "sold out instantly" rather than "not measured".
  const plotted = omitNull ? rows.filter(r => r[metricKey] != null) : rows;
  if (plotted.length === 0) {
    return <div style={{ padding: '20px 0', color: 'var(--text-light)', fontSize: 13, textAlign: 'center' }}>{emptyText || 'Not measured for this range.'}</div>;
  }

  // Each metric ranks independently — best on its own value at the top. That
  // is the highest value everywhere except a metric flagged `ascending`, where
  // lower is better (a break that fills faster beats one that drags).
  const sorted = [...plotted].sort((a, b) => ascending
    ? (a[metricKey] ?? Infinity) - (b[metricKey] ?? Infinity)
    : (b[metricKey] ?? 0) - (a[metricKey] ?? 0));
  const values = sorted.map(r => Number(r[metricKey]) || 0);
  const max = Math.max(...values);
  const min = Math.min(...values);
  // A breaker can post a negative net profit, so anchor the axis at zero and
  // let those bars run left of the baseline.
  const refValue = reference?.value ?? null;
  const domain = [Math.min(0, min, refValue ?? 0), Math.max(0, max, refValue ?? 0)];
  const gutter = compact ? GUTTER.compact : GUTTER.wide;

  // Percentages are only shown when every bar is positive. Against a net total
  // that nets off losses, a single profitable breaker can read as 533% — true
  // arithmetic, but it scans as broken, so the share is dropped instead.
  const companyTotal = Number(totals?.[metricKey]) || 0;
  const shareTotal = share && companyTotal > 0 && values.every(v => v >= 0) ? companyTotal : null;

  return (
    <ResponsiveContainer width="100%" height={Math.max(120, sorted.length * 36 + (refValue != null ? 36 : 16))}>
      <BarChart data={sorted} layout="vertical"
        margin={{ top: refValue != null ? 24 : 4, right: gutter, left: 4, bottom: 4 }}>
        <XAxis type="number" hide domain={domain} />
        <YAxis type="category" dataKey="name" hide />
        <Tooltip cursor={{ fill: 'rgba(0,0,0,0.04)' }}
          content={<RankedTooltip metricKey={metricKey} fmt={fmt} shareTotal={shareTotal} extra={extra} detail={detail} />} />
        {/* Drawn before the bars so it runs behind them and shows in the gaps. */}
        {refValue != null && (
          <ReferenceLine x={refValue} stroke="#64748b" strokeDasharray="4 3" strokeWidth={1.5}
            label={{ value: reference.label, position: 'top', fontSize: 11, fill: '#475569' }} />
        )}
        <Bar dataKey={metricKey} radius={[0, 3, 3, 0]} isAnimationActive={false}>
          {sorted.map((r, i) => (
            <Cell key={r.user_id ?? i} fill={(Number(r[metricKey]) || 0) < 0 ? 'var(--danger)' : color} />
          ))}
          <LabelList dataKey={metricKey}
            content={(props) => <BarLabel {...props} rows={sorted} metricKey={metricKey} fmt={fmt} shareTotal={shareTotal} extra={extra} gutter={gutter} domainMax={domain[1]} />} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}

// ─── Margin by mix ───────────────────────────────────────────────────

const MIX_CARD_KEY = 'mix_margins';   // the card's slot in the image export
const MIX_MARGIN_COLOR = '#8b5cf6';   // Profit Margin's colour: same measure, one mix

const MIX_MARGIN_INFO =
  'MARGIN BY MIX — pick one product mix and see every breaker\'s margin on it: '
  + 'Net Sales minus COGS, over Net Sales, across all their breaks of that mix in '
  + 'the date range. | A mix is the products and quantities in the break, freebies '
  + 'aside, named as on Insights. Freebies such as high bid packs still count in '
  + 'each break\'s COGS. | The dashed line is every breaker on that mix combined.';

const MIX_MARGIN_NOTE = 'Dashed line = all breakers on this mix. Hover a bar for averages, best and worst break, and how everyone else did.';

const breaksText = (n) => `${formatCount(n)} ${n === 1 ? 'break' : 'breaks'}`;

function mixBreakerDetail(row) {
  const lines = [`Avg per break: ${formatCurrency(row.avg_net_sales)} sales · ${formatCurrency(row.avg_net_profit)} profit`];
  if (row.break_count > 1 && row.best_margin_pct != null) {
    lines.push(`Best break ${formatPercent(row.best_margin_pct)} · worst ${formatPercent(row.worst_margin_pct)}`);
  }
  if (row.others_margin_pct != null) {
    const gap = row.margin_pct - row.others_margin_pct;
    lines.push(`Everyone else: ${formatPercent(row.others_margin_pct)} on ${breaksText(row.others_break_count)} (${formatPts(gap)})`);
  } else {
    lines.push('Nobody else ran this mix in this range.');
  }
  return lines;
}

// An auto-named mix can run to a sentence; the export's card title can't.
const shortLabel = (label, max = 60) => (label.length > max ? `${label.slice(0, max - 1).trimEnd()}…` : label);

function mixOptionText(mix) {
  return `${mix.label} — ${breaksText(mix.break_count)}, ${mix.breaker_count} ${mix.breaker_count === 1 ? 'breaker' : 'breakers'}`;
}

function mixMarginChart(mix, compact) {
  if (!mix) return null;
  return rankedBarChart({
    rows: mix.breakers, metricKey: 'margin_pct', color: MIX_MARGIN_COLOR, fmt: formatPercent, compact,
    omitNull: true, emptyText: 'No sales on this mix in this range.',
    extra: row => breaksText(row.break_count),
    detail: mixBreakerDetail,
    // With one breaker the combined line would sit exactly on their bar.
    reference: mix.breaker_count > 1 && mix.margin_pct != null
      ? { value: mix.margin_pct, label: `All breakers ${formatPercent(mix.margin_pct)}` }
      : null,
  });
}

// ─── Profit margin trend ─────────────────────────────────────────────

const MARGIN_TREND_INFO =
  'Each point is that breaker\'s Net Sales minus COGS, divided by Net Sales, for the '
  + 'day, week or month — the same sum the Profit Margin bars use, so one big break '
  + 'counts for more than a small one. | Weeks run Monday to Sunday. The first and '
  + 'last week or month only count the days inside the date range. | Periods with '
  + 'no breaks get no dot; the line runs straight across them.';

const MARGIN_TREND_NOTE = 'Dashed line = all breakers combined. Tap a name to show or hide it.';

// Fixed categorical order. A breaker keeps the same colour whatever the date
// range — the server pins each one to a slot — so narrowing the dates never
// repaints the lines that remain.
const TREND_COLORS = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const COMPANY_COLOR = '#64748b';
const COMPANY_KEY = 'all';
const TREND_KEY = 'margin_trend';   // the trend card's slot in the image export

const GROUP_OPTIONS = [
  { key: 'day',   label: 'Day' },
  { key: 'week',  label: 'Week' },
  { key: 'month', label: 'Month' },
];

// What the chart opens on for a range: a month of dailies is readable, a
// year of them is noise.
function autoGroupBy(range) {
  if (!range?.startDate || !range?.endDate) return 'day';
  const days = (new Date(`${range.endDate}T12:00:00`) - new Date(`${range.startDate}T12:00:00`)) / 86400000 + 1;
  if (days <= 31) return 'day';
  if (days <= 183) return 'week';
  return 'month';
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function periodTick(key, groupBy) {
  const [y, m, d] = key.split('-').map(Number);
  if (groupBy === 'month') return `${MONTHS[m - 1]} '${String(y).slice(2)}`;
  return `${m}/${d}`;
}

function periodTitle(key, groupBy) {
  const [y, m, d] = key.split('-').map(Number);
  if (groupBy === 'month') return `${MONTHS[m - 1]} ${y}`;
  if (groupBy === 'week') return `Week of ${m}/${d}/${String(y).slice(2)}`;
  const dow = new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short' });
  return `${dow} ${m}/${d}/${String(y).slice(2)}`;
}

// The series the chart can draw: the company line first, then each breaker in
// the order the server ranked them (by net sales for the range).
function trendSeries(trend) {
  if (!trend) return [];
  return [
    { key: COMPANY_KEY, name: 'All Breakers', color: COMPANY_COLOR, dashed: true },
    ...trend.breakers.map(b => ({
      key: `u${b.user_id}`,
      userId: b.user_id,
      name: b.name || 'Unassigned',
      color: TREND_COLORS[b.color_slot % TREND_COLORS.length],
      // Past eight breakers the hues come round again, so the repeat is drawn
      // dotted to keep two lines of one colour apart.
      dotted: b.color_slot >= TREND_COLORS.length,
    })),
  ];
}

// One row per period, each series under its own key. Null where the series
// had no sales that period, which recharts skips (and connects across).
function trendRows(trend) {
  if (!trend) return [];
  return trend.points.map(pt => {
    const row = { period: pt.period, _pt: pt, [COMPANY_KEY]: pt.all.margin_pct };
    for (const [userId, v] of Object.entries(pt.by_breaker)) row[`u${userId}`] = v.margin_pct;
    return row;
  });
}

function TrendTooltip({ active, payload, series, groupBy }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const pt = row._pt;
  // Only the lines actually drawn, and only where that line has a real point
  // this period — a connecting segment is not a value.
  const shown = new Set(payload.map(p => p.dataKey));
  const lines = series
    .filter(s => shown.has(s.key) && row[s.key] != null)
    .map(s => ({ ...s, v: s.key === COMPANY_KEY ? pt.all : pt.by_breaker[s.userId] }))
    .sort((a, b) => b.v.margin_pct - a.v.margin_pct);
  return (
    <div style={{
      background: 'var(--sidebar-bg, #1a202c)', color: 'white', padding: '8px 10px',
      borderRadius: 6, fontSize: 12, boxShadow: 'var(--shadow-lg)', minWidth: 180,
    }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{periodTitle(row.period, groupBy)}</div>
      {lines.length === 0 ? (
        <div style={{ opacity: 0.7 }}>No breaks for the lines shown.</div>
      ) : lines.map(l => (
        <div key={l.key} style={{ display: 'flex', alignItems: 'center', gap: 6, lineHeight: 1.7 }}>
          <span style={{ width: 10, height: 3, borderRadius: 2, background: l.color, flex: '0 0 auto' }} />
          <span style={{ flex: 1, fontWeight: l.key === COMPANY_KEY ? 600 : 400 }}>{l.name}</span>
          <span style={{ fontWeight: 600, marginLeft: 8 }}>{formatPercent(l.v.margin_pct)}</span>
          <span style={{ opacity: 0.7, marginLeft: 6, whiteSpace: 'nowrap' }}>
            {formatCount(l.v.break_count)} {l.v.break_count === 1 ? 'break' : 'breaks'} · {formatCurrency(l.v.net_sales)}
          </span>
        </div>
      ))}
    </div>
  );
}

// A plain function for the same reason as rankedBarChart: ChartCard needs the
// ResponsiveContainer as its own child to re-lay it out for PNG export.
function marginTrendChart({ rows, series, hidden, groupBy, compact, height = 280 }) {
  const visible = series.filter(s => !hidden.has(s.key));
  // One-point series have no segment to draw, so they need a dot to be seen
  // at all; with many periods the dots are what mark where breaks happened.
  const dotR = compact ? 2.5 : 3;
  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart data={rows} margin={{ top: 8, right: compact ? 8 : 16, left: 0, bottom: 4 }}>
        <CartesianGrid strokeDasharray="3 3" stroke="var(--border, #e2e8f0)" vertical={false} />
        <XAxis dataKey="period" tick={{ fontSize: 11 }} tickFormatter={k => periodTick(k, groupBy)}
          minTickGap={compact ? 16 : 24} interval="preserveStartEnd" />
        <YAxis tick={{ fontSize: 11 }} width={48} tickFormatter={v => `${Math.round(v)}%`}
          domain={['auto', 'auto']} />
        <ReferenceLine y={0} stroke="#94a3b8" />
        <Tooltip content={<TrendTooltip series={series} groupBy={groupBy} />}
          cursor={{ stroke: '#94a3b8', strokeDasharray: '3 3' }} />
        {visible.map(s => (
          <Line key={s.key} dataKey={s.key} name={s.name} type="linear" connectNulls
            stroke={s.color} strokeWidth={2}
            strokeDasharray={s.dashed ? '6 4' : s.dotted ? '2 3' : undefined}
            dot={{ r: dotR, fill: s.color, stroke: '#fff', strokeWidth: 1 }}
            activeDot={{ r: dotR + 2, stroke: '#fff', strokeWidth: 2 }}
            isAnimationActive={false} />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
}

function SeriesChips({ series, hidden, onToggle, onAll, onNone }) {
  const linkStyle = {
    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
    fontSize: 12, color: 'var(--accent)',
  };
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 8 }}>
      {/* `.chart-key` doubles as this chart's legend: ChartCard reads the
          buttons that are switched on when it draws the shared PNG. */}
      <div className="chart-key" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1 }}>
        {series.map(s => {
          const on = !hidden.has(s.key);
          return (
            <button key={s.key} type="button" onClick={() => onToggle(s.key)} aria-pressed={on}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '3px 9px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
                border: '1px solid var(--border)',
                background: on ? 'var(--bg-card)' : 'var(--bg)',
                color: 'var(--text)',
                opacity: on ? 1 : 0.4,
                fontWeight: s.key === COMPANY_KEY ? 600 : 400,
              }}>
              <span style={{ width: 12, height: 3, borderRadius: 2, background: s.color, display: 'inline-block' }} />
              {s.name}
            </button>
          );
        })}
      </div>
      <div style={{ display: 'flex', gap: 8, whiteSpace: 'nowrap', paddingTop: 4, fontSize: 12, color: 'var(--text-light)' }}>
        <button type="button" style={linkStyle} onClick={onAll}>All</button>
        <span>·</span>
        <button type="button" style={linkStyle} onClick={onNone}>None</button>
      </div>
    </div>
  );
}

export default function BreakersReport() {
  const isMobile = useIsMobile();
  const stageRef = useRef(null);
  const [imageStage, setImageStage] = useState(false);
  const [exportingImage, setExportingImage] = useState(false);
  const [presetLabel, setPresetLabel] = useState('Last 30D');
  // Page-local range that always opens on Last 30D, independent of whatever
  // was last picked on Financials.
  const [dateRange, setDateRange] = useState(() => getDatePreset('last_30d'));
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mixes, setMixes] = useState(null);
  const [mixError, setMixError] = useState('');
  // Kept by hash, so a mix stays chosen across date changes while it still
  // has breaks in the new range.
  const [mixPick, setMixPick] = useState(null);
  const [trend, setTrend] = useState(null);
  const [trendError, setTrendError] = useState('');
  // null = follow the range (autoGroupBy). A pick sticks until the dates
  // change, then the chart goes back to the grouping that suits the new range.
  const [groupPick, setGroupPick] = useState(null);
  const groupBy = groupPick ?? autoGroupBy(dateRange);
  // Series keys switched off. Kept across date and grouping changes so the
  // breakers you are comparing stay the ones on screen.
  const [hiddenSeries, setHiddenSeries] = useState(() => new Set());

  const load = useCallback(async () => {
    if (!dateRange?.startDate || !dateRange?.endDate) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.get('/api/admin/breakers-report', { params: dateRange });
      setRows(data.rows || []);
      setTotals(data.totals || null);
    } catch (err) {
      console.error('Load breakers report error:', err);
      setError(err.response?.data?.error || 'Failed to load breakers report');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return undefined;
    let cancelled = false;
    setMixError('');
    api.get('/api/admin/breakers-report/mix-margins', { params: dateRange })
      .then(({ data }) => { if (!cancelled) setMixes(data.mixes || []); })
      .catch(err => {
        if (cancelled) return;
        console.error('Load mix margins error:', err);
        setMixError(err.response?.data?.error || 'Failed to load margins by mix');
      });
    return () => { cancelled = true; };
  }, [dateRange]);

  // The mix most run in the range unless one has been picked.
  const selectedMix = mixes?.find(m => m.hash === mixPick) || mixes?.[0] || null;

  // Fetched separately from the bars so switching Day/Week/Month only reloads
  // this card. The previous lines stay up until the new ones arrive.
  useEffect(() => {
    if (!dateRange?.startDate || !dateRange?.endDate) return undefined;
    let cancelled = false;
    setTrendError('');
    api.get('/api/admin/breakers-report/margin-trend', { params: { ...dateRange, groupBy } })
      .then(({ data }) => { if (!cancelled) setTrend(data); })
      .catch(err => {
        if (cancelled) return;
        console.error('Load margin trend error:', err);
        setTrendError(err.response?.data?.error || 'Failed to load the margin trend');
      });
    return () => { cancelled = true; };
  }, [dateRange, groupBy]);

  const series = useMemo(() => trendSeries(trend), [trend]);
  const trendData = useMemo(() => trendRows(trend), [trend]);
  // A response for another grouping can still be on screen while the new one
  // loads; label its axis by what it actually holds.
  const trendGroupBy = trend?.groupBy || groupBy;
  const visibleSeries = useMemo(() => series.filter(s => !hiddenSeries.has(s.key)), [series, hiddenSeries]);

  const toggleSeries = useCallback((key) => {
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }, []);

  // The export mirrors the card as it stands: only the lines switched on, at
  // the grouping picked. With every line off it is left out of the picture.
  const exportTrend = visibleSeries.length > 0 && trendData.length > 0;
  // Like the page, it closes the report on a row of its own. The mix card
  // goes in showing whichever mix is picked.
  const exportMix = !!selectedMix?.breakers.some(b => b.margin_pct != null);
  const exportCards = useMemo(() => {
    const cards = METRICS.flatMap(m => (m.key === 'mix_vs_par_pts' && exportMix
      ? [m, { key: MIX_CARD_KEY, label: `Margin by Mix · ${shortLabel(selectedMix.label)}`, note: 'Dashed line = all breakers on this mix.' }]
      : [m]));
    if (exportTrend) {
      cards.push({
        key: TREND_KEY,
        fullWidth: true,
        label: `Profit Margin Trend · by ${GROUP_OPTIONS.find(o => o.key === trendGroupBy)?.label}`,
        note: 'Net Sales minus COGS, divided by Net Sales, per period. Dashed line = all breakers combined.',
        legend: visibleSeries.map(s => ({ text: s.name, color: s.color, dashed: !!s.dashed })),
      });
    }
    return cards;
  }, [exportTrend, trendGroupBy, visibleSeries, exportMix, selectedMix]);

  // Render the charts off-screen at a fixed desktop width and compose the PNG
  // from them, rather than capturing the live page: the export should look the
  // same from a phone as from a laptop, and it drops the per-card share /
  // download / expand buttons, which mean nothing in a picture.
  const exportImage = useCallback(async () => {
    if (!totals || rows.length === 0) return;
    setExportingImage(true);
    setImageStage(true);
    try {
      await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
      await waitForCharts(stageRef.current, exportCards.length);

      const svgs = new Map();
      for (const m of exportCards) {
        const svg = stageRef.current?.querySelector(`[data-metric="${m.key}"] svg.recharts-surface`);
        if (svg) svgs.set(m.key, svg);
      }
      if (svgs.size === 0) throw new Error('the charts did not render for export');

      const blob = await composeReportImage({
        svgs,
        metrics: exportCards,
        tiles: summaryTiles(totals),
        range: dateRange,
        presetLabel,
        footnote: `Net Profit is Net Sales minus COGS. ${NET_PROFIT_INFO.split('|')[1].trim()}`,
      });
      downloadBlob(blob, `breakers-report-${dateRange.startDate}-to-${dateRange.endDate}.png`);
    } catch (err) {
      console.error('Export image error:', err);
      setError(err.message || 'Could not build the image');
    } finally {
      setImageStage(false);
      setExportingImage(false);
    }
  }, [rows, totals, dateRange, presetLabel, exportCards]);

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Breakers Report</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <ExportButton endpoint="/api/admin/breakers-report/export" params={dateRange}
            filename={`breakers-report-${dateRange.startDate}-to-${dateRange.endDate}.csv`} />
          <button className="btn btn-secondary btn-sm" onClick={exportImage}
            disabled={exportingImage || loading || rows.length === 0}>
            {exportingImage ? 'Building image…' : 'Export as Image'}
          </button>
        </div>
      </div>

      <DateRangeFilter initial="last_30d"
        onChange={(range, meta) => { setDateRange(range); setPresetLabel(meta ? meta.label : null); setGroupPick(null); }} />

      {error && <div className="card" style={{ marginBottom: 12, color: 'var(--danger)' }}>{error}</div>}

      {totals && (
        <div className="kpi-grid">
          {summaryTiles(totals).map(t => (
            <KPICard key={t.label} label={t.label} value={t.value} info={t.info || undefined} />
          ))}
        </div>
      )}

      {loading ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--text-light)' }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="card" style={{ padding: 24, textAlign: 'center', color: 'var(--text-light)' }}>
          No breaks hosted in this date range.
        </div>
      ) : (
        <div className="charts-grid">
          {METRICS.map(m => (
            <React.Fragment key={m.key}>
              <ChartCard titleText={m.label} note={m.note}
                title={<>{m.label}{m.info && <InfoTip text={m.info} label={m.label} />}</>}>
                {rankedBarChart({ rows, totals, metricKey: m.key, color: m.color, fmt: m.fmt, share: m.share, compact: isMobile, ascending: m.ascending, omitNull: m.omitNull, detail: m.detail, emptyText: m.emptyText })}
              </ChartCard>
              {/* Follows the par chart: that one sums every mix up, this one
                  opens a single mix to compare breaker against breaker. */}
              {m.key === 'mix_vs_par_pts' && (
                <ChartCard titleText="Margin by Mix" note={selectedMix ? MIX_MARGIN_NOTE : undefined}
                  title={<>Margin by Mix<InfoTip text={MIX_MARGIN_INFO} label="Margin by Mix" /></>}
                  controls={mixes?.length > 0 && (
                    <select value={selectedMix?.hash || ''} onChange={e => setMixPick(e.target.value)}
                      aria-label="Product mix"
                      // A fixed width: left to size itself, a select grows to its
                      // longest option, and an auto-named mix can run to a
                      // sentence, pushing the card off a phone screen.
                      style={{ width: isMobile ? '100%' : 300, maxWidth: '100%' }}>
                      {mixes.map(mix => <option key={mix.hash} value={mix.hash}>{mixOptionText(mix)}</option>)}
                    </select>
                  )}>
                  {mixError ? (
                    <div style={{ padding: '20px 0', color: 'var(--danger)', fontSize: 13, textAlign: 'center' }}>{mixError}</div>
                  ) : !mixes ? (
                    <div style={{ padding: '20px 0', color: 'var(--text-light)', fontSize: 13, textAlign: 'center' }}>Loading…</div>
                  ) : mixes.length === 0 ? (
                    <div style={{ padding: '20px 0', color: 'var(--text-light)', fontSize: 13, textAlign: 'center' }}>No breaks with products entered in this range.</div>
                  ) : mixMarginChart(selectedMix, isMobile)}
                </ChartCard>
              )}
            </React.Fragment>
          ))}
          {/* Full width on a row of its own, so its height never stretches a
              bar chart sitting beside it. */}
          <ChartCard titleText="Profit Margin Trend" note={MARGIN_TREND_NOTE} style={{ gridColumn: '1 / -1' }}
            title={<>Profit Margin Trend<InfoTip text={MARGIN_TREND_INFO} label="Profit Margin Trend" /></>}
            controls={(
              <div className="chart-toggle">
                {GROUP_OPTIONS.map(opt => (
                  <button key={opt.key} type="button" onClick={() => setGroupPick(opt.key)}
                    className={`date-preset ${groupBy === opt.key ? 'active' : ''}`}
                    style={{ padding: '4px 12px', fontSize: 12 }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}>
            {trendError ? (
              <div style={{ padding: '20px 0', color: 'var(--danger)', fontSize: 13, textAlign: 'center' }}>{trendError}</div>
            ) : !trend ? (
              <div style={{ padding: '20px 0', color: 'var(--text-light)', fontSize: 13, textAlign: 'center' }}>Loading…</div>
            ) : trend.breakers.length === 0 ? (
              <div style={{ padding: '20px 0', color: 'var(--text-light)', fontSize: 13, textAlign: 'center' }}>No sales to chart a margin for in this range.</div>
            ) : (
              <>
                <SeriesChips series={series} hidden={hiddenSeries} onToggle={toggleSeries}
                  onAll={() => setHiddenSeries(new Set())}
                  onNone={() => setHiddenSeries(new Set(series.map(s => s.key)))} />
                {visibleSeries.length === 0 ? (
                  <div style={{ padding: '20px 0', color: 'var(--text-light)', fontSize: 13, textAlign: 'center' }}>
                    Every line is switched off. Pick a name above to show it.
                  </div>
                ) : marginTrendChart({ rows: trendData, series, hidden: hiddenSeries, groupBy: trendGroupBy, compact: isMobile, height: isMobile ? 280 : 340 })}
              </>
            )}
          </ChartCard>
        </div>
      )}

      {/* Off-screen stage: the same charts laid out at the export's fixed
          desktop width, so the picture never inherits the current viewport. */}
      {imageStage && (
        <div ref={stageRef} aria-hidden="true"
          style={{ position: 'fixed', left: -20000, top: 0, width: REPORT_FULL_CHART_W, background: '#fff', pointerEvents: 'none' }}>
          {exportMix && (
            <div data-metric={MIX_CARD_KEY} style={{ width: REPORT_CHART_W }}>
              {mixMarginChart(selectedMix, false)}
            </div>
          )}
          {exportTrend && (
            <div data-metric={TREND_KEY} style={{ width: REPORT_FULL_CHART_W }}>
              {marginTrendChart({ rows: trendData, series, hidden: hiddenSeries, groupBy: trendGroupBy, compact: false, height: 320 })}
            </div>
          )}
          {METRICS.map(m => (
            <div key={m.key} data-metric={m.key} style={{ width: REPORT_CHART_W }}>
              {rankedBarChart({ rows, totals, metricKey: m.key, color: m.color, fmt: m.fmt, share: m.share, compact: false, ascending: m.ascending, omitNull: m.omitNull, detail: m.detail, emptyText: m.emptyText })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
